import { queryOptions, useQuery } from '@tanstack/react-query'
import { useUser, useIsUserExempt } from '@/features/account/services/User/hooks'
import {
  fetchBanners,
  fetchSubscriptionAuxiliaryBanners,
} from '@/features/shop/services/Banners/fetchers'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'

const BannersQuery = 'Banners'
type Params = { location_id: number; store_id: number; metro_id: number }
export type BannersKey = readonly [typeof BannersQuery, Params]

const bannersQueryOptions = (params: Params) =>
  queryOptions({
    queryKey: [BannersQuery, params],
    queryFn: fetchBanners,
  })

export const useQueryBanners = ({
  storeLocationId,
  storeId,
  metroId,
}: {
  storeLocationId?: number
  storeId?: number
  metroId: number
}) => {
  const { userId } = useSession()

  return useQuery({
    ...bannersQueryOptions({
      store_id: storeId ?? 0,
      location_id: storeLocationId ?? 0,
      metro_id: metroId,
    }),
    enabled: Boolean(userId && metroId),
  })
}

const MembershipEntryPointBannerQuery = 'MembershipEntryPointBanner'
export type MembershipEntryPointBannerKey = readonly [
  typeof MembershipEntryPointBannerQuery,
  { membership_status: string },
]
const membershipEntryPointBannerQueryOptions = (membership_status: string) =>
  queryOptions({
    queryKey: [MembershipEntryPointBannerQuery, { membership_status }] as const,
    queryFn: fetchSubscriptionAuxiliaryBanners,
  })

export const useQueryMembershipEntryPointBanner = (
  enabled: boolean,
  doNotNotifyOnChange = false,
) => {
  const { isSessionLoading, isSessionValid } = useSession()
  const user = useUser()
  const isExempt = useIsUserExempt()
  const hasMembership = Boolean(user?.subscription?.id)
  let membership_status: 'ppo' | 'exempt' | 'subscription' = 'ppo'

  if (isExempt) {
    membership_status = 'exempt'
  } else if (hasMembership) {
    membership_status = 'subscription'
  }

  return useQuery({
    ...membershipEntryPointBannerQueryOptions(membership_status),
    ...(doNotNotifyOnChange ? { notifyOnChangeProps: [] } : {}),
    // Wait for session to resolve; guests fetch immediately, authed users wait for user data.
    enabled: enabled && !isSessionLoading && (!isSessionValid || Boolean(user?.id)),
  })
}
